/**
 * @fileoverview ArcMosaico sign-in screen (AR-792)
 * @description Rendered instead of the legacy SessionNew when the
 * `arc-mosaico-project` flag is on. Reuses the legacy session hook for the
 * login logic; visuals follow arcmosaico.ai (header bar, rounded hero banner
 * with the legacy illustration, centered form card).
 */

import { LumiarToastContainer, toastOptions } from '@legacy/common/_components/LumiarToast'
import {
  Anchor,
  Button,
  Checkbox,
  Divider,
  Group,
  PasswordInput,
  Space,
  Text,
  TextInput,
} from '@mantine/core'
import { useForm } from '@mantine/form'
import { useDisclosure } from '@mantine/hooks'
import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Link, useSearchParams } from 'react-router-dom'
import { toast } from 'react-toastify'

import { GoogleButton } from '@legacy/pages/Account/SignIn/session-new-google-button-component'
import { useSessionNew } from '@legacy/pages/Account/SignIn/session-new-hook'
import { ArcAuthHeader } from 'components/ArcAuthHeader'
import { ArcAuthShell } from 'components/ArcAuthPage'
import { useNotification } from 'context/useNotificationContext'
import { useSpecificFeature } from 'hooks'
import { EmailConfirmedModal } from 'pages/TeacherSignup/components/EmailConfirmedModal'
import { CleverButton } from './CleverButton'
import { ArcTitle, Form, FormCard, FormColumn, Subtitle } from './styles'

export const ArcSessionNew = () => {
  const { t } = useTranslation()
  const sessionData = useSessionNew()
  const { payload, loading, check, logIn } = sessionData || {}
  const [visible, { toggle }] = useDisclosure(false)
  const [searchParams] = useSearchParams()
  const { handleNotification } = useNotification()
  const { enabled: isSignupButtonEnabled } = useSpecificFeature('arc-mosaico-signup')
  const [showEmailConfirmedModal, setShowEmailConfirmedModal] = useState(false)

  const form = useForm({
    initialValues: { email: payload?.login || '', password: payload?.password || '' },
  })

  const handleSubmit = (values: typeof form.values) => {
    if (payload && logIn) {
      logIn(values.email, values.password)
    }
  }

  useEffect(() => {
    const paramType: string | null = searchParams.get('success')

    switch (paramType) {
      case 'change_sucess':
        handleNotification(true, t('forgot_password.change_sucess'), true, 'success')
        break
      case 'not_changed':
        handleNotification(true, t('forgot_password.not_changed'), true, 'error')
        break
      default:
        break
    }
  }, [])

  useEffect(() => {
    const sendResetPasswordInstructions = searchParams.get('send_reset_password_instructions')
    if (sendResetPasswordInstructions === 'true') toast.success(t('send_instructions'), toastOptions)
  }, [])

  useEffect(() => {
    const emailConfirmed = searchParams.get('email_confirmed')
    if (emailConfirmed === 'true') {
      setShowEmailConfirmedModal(true)
      // Clean query param from URL
      const url = new URL(window.location.href)
      url.searchParams.delete('email_confirmed')
      window.history.replaceState({}, '', url.toString())
    }
  }, [])

  return (
    <ArcAuthShell>
      <LumiarToastContainer />
      <ArcAuthHeader />
      <FormColumn>
        <ArcTitle>
          {t('arc_signin.title_prefix')} Arc<span>Mosaico</span>
        </ArcTitle>
        <Subtitle>{t('arc_signin.subtitle')}</Subtitle>
        <FormCard>
          <Form onSubmit={form.onSubmit(handleSubmit)}>
            <TextInput
              id='input-email'
              size='md'
              aria-label={t('account_signIn_placeholder_login')}
              label={t('account_signIn_placeholder_login')}
              name='login'
              disabled={loading}
              {...form.getInputProps('email')}
            />
            <PasswordInput
              mt='md'
              id='input-password'
              size='md'
              label={t('account_signIn_placeholder_password')}
              aria-label={t('account_signIn_placeholder_password')}
              name='password'
              disabled={loading}
              visible={visible}
              onVisibilityChange={toggle}
              {...form.getInputProps('password')}
            />
            <Group mt='md' position='apart'>
              <Checkbox
                label={t('account_signIn_remember')}
                aria-label={t('account_signIn_remember')}
                labelPosition='right'
                onChange={check}
                id='rememberMe'
                name='rememberMe'
                size='md'
                checked={payload?.rememberMe || false}
                disabled={loading}
              />
              <Anchor component={Link} to='/forgot-password' aria-label={t('account_signIn_forgot_password')}>
                {t('account_signIn_forgot_password')}
              </Anchor>
            </Group>

            <Button
              fullWidth
              mt='lg'
              variant='filled'
              disabled={loading}
              type='submit'
              size='lg'
              aria-label={t('account_signIn_login')}
            >
              {t('account_signIn_login')}
            </Button>
          </Form>
          <Divider my='md' label={t('account_signIn_divider')} labelPosition='center' />
          <GoogleButton disabled={loading} />
          <CleverButton disabled={loading} />

          {isSignupButtonEnabled && (
            <>
              <Space h='md' />
              <Text align='center' color='dimmed' size='sm'>
                {t('teacher_signup.no_account')}
              </Text>
              <Button
                component={Link}
                to='/account/teacher-signup'
                fullWidth
                mt='md'
                variant='outline'
                disabled={loading}
                size='lg'
              >
                {t('teacher_signup.sign_up_now')}
              </Button>
            </>
          )}
        </FormCard>
      </FormColumn>

      <EmailConfirmedModal
        opened={showEmailConfirmedModal}
        onClose={() => setShowEmailConfirmedModal(false)}
      />
    </ArcAuthShell>
  )
}
